/**
 * PasswordExpiryDialog — global popup shown when the logged-in user's
 * password is within `warning_window_days` of expiry. Skippable from
 * T-5..T-2; mandatory at T-1 and below (hides the Skip button).
 *
 * Reads `user.password_status` from AuthContext.
 */
import { useState, useEffect } from "react";
import { ShieldAlert, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { scorePassword, validatePassword } from "../lib/passwordStrength";

const SESSION_SKIP_KEY = "lawos.pwexp.skipped";

export default function PasswordExpiryDialog() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);

    const status = user?.password_status;
    const mustChange = !!status?.must_change_now;
    const daysLeft = status ? Math.max(0, Math.ceil(status.days_remaining)) : null;

    useEffect(() => {
        if (!status) return;
        if (!status.in_warning_window && !status.expired) {
            setOpen(false);
            return;
        }
        if (mustChange) {
            setOpen(true);  // mandatory — always show
            return;
        }
        // Skippable — show unless user clicked skip in this session
        const skipped = sessionStorage.getItem(SESSION_SKIP_KEY) === "1";
        setOpen(!skipped);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status?.expires_at, mustChange]);

    if (!status || (!status.in_warning_window && !status.expired)) return null;
    if (!open) return null;

    const score = scorePassword(next);
    const matches = next && next === confirm;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const err = validatePassword(next);
        if (err) { toast.error(err); return; }
        if (next !== confirm) { toast.error("New password and confirmation don't match."); return; }
        if (current === next) { toast.error("New password must differ from current."); return; }
        setLoading(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Password changed. Please sign in again.");
            sessionStorage.removeItem(SESSION_SKIP_KEY);
            setTimeout(() => { window.location.href = "/login"; }, 800);
        } catch (e2) {
            toast.error(apiError(e2));
        } finally { setLoading(false); }
    };

    const handleSkip = () => {
        sessionStorage.setItem(SESSION_SKIP_KEY, "1");
        setOpen(false);
        toast(`Reminder: your password expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}.`);
    };

    return (
        <div className="fixed inset-0 z-[80] bg-background/85 backdrop-blur-sm flex items-center justify-center p-4"
             data-testid="password-expiry-dialog">
            <div className="bg-card border border-primary/40 max-w-md w-full p-6 shadow-2xl">
                <div className="flex items-center gap-3 mb-4">
                    <div className={`p-2 ${mustChange ? "bg-red-500/10 border border-red-500/40" : "bg-amber-500/10 border border-amber-500/40"}`}>
                        <ShieldAlert className={`w-5 h-5 ${mustChange ? "text-red-400" : "text-amber-400"}`} strokeWidth={1.5}/>
                    </div>
                    <div>
                        <div className="overline" style={{color: mustChange ? "#ef4444" : "#f59e0b"}}>
                            {mustChange ? "Password change required" : "Password expiring soon"}
                        </div>
                        <div className="text-xs text-foreground mt-1">
                            {status.expired
                                ? "Your password has expired. You must change it before continuing."
                                : (mustChange
                                    ? `Your password expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"} — change it now to continue.`
                                    : `Your password expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}. Change now or skip until next login.`)}
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3 mt-5">
                    <Field label="Current password">
                        <input type={show ? "text" : "password"} value={current}
                               onChange={e => setCurrent(e.target.value)} required autoFocus
                               data-testid="pwexp-current"
                               className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm"/>
                    </Field>
                    <Field label="New password">
                        <div className="relative">
                            <input type={show ? "text" : "password"} value={next}
                                   onChange={e => setNext(e.target.value)} required
                                   data-testid="pwexp-new"
                                   className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 pr-10 text-sm"/>
                            <button type="button" onClick={() => setShow(s => !s)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    data-testid="pwexp-toggle-visibility">
                                {show ? <EyeOff className="w-4 h-4" strokeWidth={1.5}/> : <Eye className="w-4 h-4" strokeWidth={1.5}/>}
                            </button>
                        </div>
                        {next && (
                            <div className="flex items-center gap-2 mt-1.5" data-testid="pwexp-strength">
                                <div className="flex-1 h-1 bg-border overflow-hidden">
                                    <div className="h-full transition-all" style={{width: `${((score.score+1)/6)*100}%`, background: score.color}}/>
                                </div>
                                <div className="text-[10px] font-medium" style={{color: score.color}}>{score.label}</div>
                            </div>
                        )}
                        {next && <div className="text-[10px] text-muted-foreground mt-1">{score.hint}</div>}
                    </Field>
                    <Field label="Confirm new password">
                        <input type={show ? "text" : "password"} value={confirm}
                               onChange={e => setConfirm(e.target.value)} required
                               data-testid="pwexp-confirm"
                               className={`w-full bg-background border outline-none px-3 py-2 text-sm ${
                                   confirm ? (matches ? "border-emerald-500/60" : "border-red-500/60") : "border-border focus:border-primary"}`}/>
                        {confirm && !matches && <div className="text-[10px] text-red-400 mt-1">Passwords don&apos;t match.</div>}
                        {confirm && matches && <div className="text-[10px] text-emerald-400 mt-1">✓ Matches</div>}
                    </Field>

                    <div className="flex items-center gap-3 mt-5">
                        {!mustChange && (
                            <button type="button" onClick={handleSkip}
                                    data-testid="pwexp-skip"
                                    className="px-4 py-2 text-xs text-muted-foreground hover:text-foreground border border-border hover:border-border">
                                Skip · remind me next login
                            </button>
                        )}
                        <button type="submit" disabled={loading || !current || !next || !matches}
                                data-testid="pwexp-submit"
                                className="flex-1 bg-primary text-primary-foreground hover:bg-primary disabled:opacity-40 px-4 py-2 text-xs font-medium uppercase tracking-[0.16em]">
                            {loading ? "Changing…" : "Change password"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

const Field = ({ label, children }) => (
    <div>
        <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5 block">{label}</label>
        {children}
    </div>
);
